import { graphql } from 'gatsby';
import { get, kebabCase, startCase } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { P } from '@upsolve/ui';

import * as CONTENT_CREATION_METHODS from '../components/analytics/contentCreationMethods';
import * as CONTENT_GROUPS from '../components/analytics/contentGroups';
import Article from '../components/Layout/Article.div';
import AuthorBio from '../components/Layout/AuthorBio';
import AuthorsBlock from '../components/Layout/AuthorsBlock';
import CTASideBySideOfferings from '../components/CTA/CTASideBySideOfferings';
import HeroFilerCta from '../components/CTA/HeroFilerCta';
import HorizontalRule from '../components/Layout/HorizontalRule.hr'
import MainFooter from '../components/Layout/Footer'
import MainHeader from '../components/Layout/Header'
import SEO from '../components/SEO/SEO';
import TopLevelLayout from '../components/Layout/TopLevelLayout';
import UpsolveFilersCount from '../components/ProofBuilders/UpsolveFilersCount';
import VideoInContext from '../components/Media/VideoInContext';
import checkForInlineCta from '../components/Contentful/checkForInlineCta';
import getHeadersFromRichText from '../components/Contentful/getHeadersFromRichText';
import getWordCountFromRichText from '../components/Contentful/getWordCountFromRichText';
import richTextNodeToComponent from '../components/Contentful/richTextNodeToComponent';
import richTextNodesInSections from '../components/Contentful/richTextNodesInSections';
import { Ampify } from '../components/AMP/AmpContext';
import { H1, H6 } from '../components/Type/Display.h';
import trackPageView from '../components/analytics/trackPageView';
import withScrollWatcher from '../components/analytics/withScrollWatcher';
import trackPageScroll from '../components/analytics/trackPageScroll';
import ContentSectionFooter from '../components/Layout/ContentSectionFooter';
import getInlineCTAComponents from '../components/CTA/getInlineCTAComponents';
import withGeoLocation from '../components/maps/withGeoLocation';

const contentGroup = CONTENT_GROUPS.LEARN_ARTICLE;
const templateProps = {
  pageComponentName: 'LearnArticle',
  pageComponentVersion: 1,
};
const CTAS = getInlineCTAComponents();

/**
 * LearnArticle
 * - v0: Where we began.
 * - v1: Added geolocation region to persistent cta text
 *
 * @component
 * @version 1
 */
const LearnArticle = (props) => {
  const { geoLocation, pageContext, path, scrollWatcher } = props;
  const { amp } = pageContext;
  const contentNode = props.data.contentfulLearnArticle;
  const seoNode = contentNode.seo;
  const content = get(contentNode, 'content.json');
  const sectionHeaders = getHeadersFromRichText(content);
  const { title, video } = contentNode;
  const articleTags = contentNode.tags || [];
  const hasInlineCta = checkForInlineCta(content.json);
  const cta = `File Bankruptcy${geoLocation && geoLocation.regionName ? ` in ${geoLocation.regionName}` : ' Online'}`;
  const authors = (contentNode.authors || [contentNode.author])
    .filter((author) => author && author.name != null);
  const wordCount = getWordCountFromRichText(get(props, 'data.contentfulLearnArticle.content.json', []));

  // Tracking
  const trackingProps = {
    contentCreationMethod: CONTENT_CREATION_METHODS.WRITTEN,
    contentGroup,
    contentLocalized: get(props, 'data.page.contentTraits.contentLocalized')
      || get(props, 'data.page.locality') != null,
    contentTopics: articleTags.map(tag => tag.value),
    searchTermTarget: get(props, 'data.page.contentTraits.searchTermTarget'),
    wordCount,
    ...templateProps,
  };
  // On Mount
  useEffect(() => { trackPageView(trackingProps); }, []);
  // - Page Scroll
  const [scrollIntervalCache, setScrollIntervalCache] = useState([0]);
  useEffect(() => {
    if (Math.max(...scrollIntervalCache) < scrollWatcher.scrollInterval) {
      setScrollIntervalCache(scrollIntervalCache.concat(scrollWatcher.scrollInterval));
      trackPageScroll(trackingProps);
    }
  }, [scrollWatcher.scrollInterval]);

  // Comp
  const children = (
    <TopLevelLayout showPersistentCta persistentCtaText={cta}>
      <SEO
        seo={seoNode}
        contentNode={({
          ...contentNode,
          seoImage: contentNode.seoImage || contentNode.coverImage,
          video,
        })}
      />
      <MainHeader contentGroup={contentGroup} />
      <Article>
        <nav className="article__breadcrumb" aria-label="Breadcrumb" data-test-id="breadcrumb">
          <ol>
            <li>
              <a aria-busy="false" href="/learn/">Learning Center</a>
            </li>
            {articleTags.length > 0 && (
              <li>
                {articleTags
                  .filter(tag => tag.type === 'category')
                  .map((tag) => (
                    <a key={tag.value} aria-busy="false" href={`/learn/category/${kebabCase(tag.value)}/`}>
                      {tag.label}
                    </a>
                  ))}
              </li>
            )}
          </ol>
        </nav>
        <div className="article__body">
          <article className="article__content">
            <div className="content-hero">
              <header className="content-hero__header">
                <H1 as='h1'>{contentNode.title}</H1>
                <UpsolveFilersCount />
                <HorizontalRule />
                <AuthorsBlock
                  authors={authors}
                  createdAt={contentNode.createdAt}
                  updatedAt={contentNode.updatedAt}
                />
              </header>
            </div>
            <div className="content-body">
              <div className="content-body__container">
                {video != null && (
                  <section className="content-video">
                    <VideoInContext video={video} />
                  </section>
                )}
                {get(contentNode, 'articleExcerpt.articleExcerpt') != null && (
                  <div className="content-body__lead">
                    <div className="summary">
                      <b>Summary</b>
                      <P>{get(contentNode, 'articleExcerpt.articleExcerpt')}</P>
                    </div>
                  </div>
                )}
                {sectionHeaders.length > 1 && (
                  <nav>
                    <H6>Read on to learn more about:</H6>
                    <ul className="content-body__nav">
                      {Array.prototype.map.call(sectionHeaders, header => (
                        <li key={header}><a aria-busy="false" href={`#${kebabCase(header)}`}>{header}</a></li>
                      ))}
                    </ul>
                  </nav>
                )}
                {richTextNodesInSections(content).length === 1
                  ? Array.prototype.map.call(richTextNodesInSections(content)[0], (c, i) => richTextNodeToComponent(c, i, null, { contentGroup }))
                  : Array.prototype.map.call(richTextNodesInSections(content), (section, sectionIndex, sectionArray) => (
                    <Fragment key={sectionIndex}>
                      <section>
                        {Array.prototype.map.call(section, (c, i) => richTextNodeToComponent(c, i, null, { contentGroup }))}
                        {(sectionIndex !== 0 && section.length > 1) && <ContentSectionFooter />}
                      </section>
                      {(hasInlineCta === false && Math.floor(sectionArray.length * 0.35) === sectionIndex) &&
                        ((CTA) => <CTA key={props.pageContext.inlineCTAOrder[0]} dataTestId="inline-content-cta" />)(CTAS[props.pageContext.inlineCTAOrder[0]])}
                      {(hasInlineCta === false && wordCount > 1000 && Math.floor(sectionArray.length * 0.7) === sectionIndex) &&
                        ((CTA) => <CTA key={props.pageContext.inlineCTAOrder[1]} dataTestId="inline-content-cta" />)(CTAS[props.pageContext.inlineCTAOrder[1]])}
                    </Fragment>
                  ))}
                {authors.length > 0 && (
                  <div className="author-bios">
                    <b>About the {`author${authors.length > 1 ? 's': ''}`}</b>
                    {Array.prototype.map.call(authors, (author) => (
                      <AuthorBio key={author.name} author={author} />
                    ))}
                  </div>
                )}
              </div>
              <aside className="article__sidebar">
                <div className="sidebar-hero__cta">
                  <HeroFilerCta contentGroup={contentGroup} />
                </div>
              </aside>
            </div>
          </article>
        </div>
      </Article>
      <CTASideBySideOfferings contentGroup={contentGroup} />
      <MainFooter showArticles />
    </TopLevelLayout>
  );

  return amp === true
    ? <Ampify>{children}</Ampify>
    : children;
}

export const pageQuery = graphql`
  query LearnArticleBySlug($slug: String!) {
    contentfulLearnArticle(slug: { eq: $slug }) {
      slug
      title
      publishedAt
      tags {
        value
        label
        type
      }
      coverImage {
        image {
          file {
            url
          }
          fluid(maxWidth: 960) {
            ...GatsbyContentfulFluid
          }
        }
      }
      articleExcerpt {
        articleExcerpt
      }
      video {
        ...VideoNode
      }
      author {
        ...AuthorNode
      }
      authors {
        ...AuthorNode
      }
      content {
        json
      }
      seo {
        ...SeoNode
      }
      createdAt
      updatedAt
    }
  }
`;

export default withScrollWatcher(withGeoLocation(LearnArticle));
